import { Toaster } from "src/components/ui/Toaster";
import { Toaster as Sonner } from "src/components/ui/Sonner";
import { TooltipProvider } from "src/components/ui/Tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { useEffect, useRef } from "react";
import { ROUTES } from "./routes/RouteConfig";
import { ProtectedRouteWrapper } from "src/components/auth/ProtectedRouteWrapper";
import LoadingSpinner from "./components/LoadingSpinner";
import { useAuthStore } from "src/store/authStore";

// Import all page components
import Index from "./pages/Index";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import Articles from "./pages/resources/Articles";
import BookDemo from "./pages/tutor/BookDemo";
import BookDemoBySearch from "./pages/sessions/BookDemoBySearch";
import StudentDashboard from "./pages/student/Dashboard";
import StudentProfile from "./pages/student/Profile";
import StudentSchedulePage from "./pages/student/Schedule";
import StudentJourney from "./pages/student/Journey";
import StudentCurriculumProgress from "./pages/student/CurriculumProgress";
import StudentInsights from "./pages/student/Insights";
import StudentMaterials from "./pages/student/Materials";
import RequestBooking from "./pages/student/RequestBooking";
import Products from "src/pages/student/Products";
import BillingHistory from "src/pages/student/BillingHistory";
import Subscriptions from "src/pages/student/subscriptions/SubscriptionList";
import AccountPreferences from "src/pages/student/AccountPreferences";
import NotificationsPage from "src/pages/student/Notifications";
import StudentOnboarding from "./pages/student/Onboarding";
import NewSubscription from "./pages/student/subscriptions/create/Create";
import NewSubscriptionSelect from "./pages/student/subscriptions/create/Select";
import NewSubscriptionConfigure from "./pages/student/subscriptions/create/Configure";
import NewSubscriptionPricing from "./pages/student/subscriptions/create/Pricing";
import NewSubscriptionPurchase from "./pages/student/subscriptions/create/Purchase";
import StudentUpcomingSessions from "./pages/student/sessions/UpcomingSessions";
import StudentPastSessions from "./pages/student/sessions/PastSessions";
import Inquiry from "./pages/Inquiry";
import Feedback from "./pages/Feedback";
import BecomeTutor from "./pages/BecomeTutor";
import NotFound from "./pages/NotFound";
import { config } from "./config/environment";
import Inquiries from "./pages/admin/Inquiries";
import DemoBookings from "./pages/admin/DemoBookings";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLiveView from "./pages/admin/LiveView";
import ForgotPassword from "src/pages/auth/ForgotPassword";
import ForgotPasswordConfirmation from "src/pages/auth/ForgotPasswordConfirmation";
import ResetPassword from "src/pages/auth/ResetPassword";
import TutorManagement from "src/pages/admin/TutorManagement";
import UserManagement from "src/pages/admin/UserManagement";
// User Management Pages
import CreateUser from "src/pages/admin/users/CreateUser";
import ListUsers from "src/pages/admin/users/ListUsers";
import ViewUser from "src/pages/admin/users/ViewUser";
import EditUser from "src/pages/admin/users/EditUser";
import DeleteUser from "src/pages/admin/users/DeleteUser";
// Tutor Management Pages
import CreateTutor from "src/pages/admin/tutors/CreateTutor";
import ListTutors from "src/pages/admin/tutors/ListTutors";
import ViewTutor from "src/pages/admin/tutors/ViewTutor";
import EditTutor from "src/pages/admin/tutors/EditTutor";
import DeleteTutor from "src/pages/admin/tutors/DeleteTutor";
import ApprovalQueue from "src/pages/admin/tutors/ApprovalQueue";
import Terms from "src/pages/Terms";
import Privacy from "src/pages/Privacy";
import HowItWorks from "src/pages/HowItWorks";
import Resources from "src/pages/Resources";
import Courses from "src/pages/Courses";
import PythonCourse from "src/pages/courses/Python";
import JavaScriptCourse from "src/pages/courses/JavaScript";
import About from "./pages/About";
import Careers from "./pages/Careers";
import Pricing from "./pages/Pricing";
import Tutors from "./pages/Tutors";
import Contact from "./pages/Contact";
import ScrollToTop from "src/components/ScrollToTop";
import SessionManagement from "./pages/admin/SessionManagement";
import ScheduleSession from "./pages/admin/sessions/ScheduleSession";
import EditSession from "./pages/admin/sessions/EditSession";
import JoinSession from "./pages/sessions/JoinSession";
import TawkToWidget from "src/components/TawkToWidget";
import AddUsersToSession from "./pages/admin/sessions/AddUsersToSession";
import RemoveUserFromSession from "./pages/admin/sessions/RemoveUserFromSession";
import SessionsList from "./pages/admin/sessions/SessionsList";
import SessionAnalytics from "./pages/admin/sessions/SessionAnalytics";
import SessionConfiguration from "./pages/admin/sessions/SessionConfiguration";
import BatchManagement from "src/pages/admin/BatchManagement";
import CreateBatch from "src/pages/admin/batches/CreateBatch";
import ViewBatches from "src/pages/admin/batches/ViewBatches";
import EditBatch from "src/pages/admin/batches/EditBatch";
import DeleteBatch from "src/pages/admin/batches/DeleteBatch";
import Enrollments from "src/pages/admin/Enrollments";
import ViewEnrollments from "src/pages/admin/ViewEnrollments";
import ManualEnrollment from "src/pages/admin/ManualEnrollment";
import EnrollmentDetail from "src/pages/admin/EnrollmentDetail";
import CurriculumCatalog from "src/pages/admin/CurriculumCatalog";
import ExtendBatchSessions from "src/pages/admin/batches/ExtendBatchSessions";
import ConfirmEmail from "./pages/auth/ConfirmEmail";
import ConfirmationCallback from "./components/auth/ConfirmationCallback";
import UnderMaintenance from "src/pages/UnderMaintenance";
import TutorOnboarding from "./pages/tutor/Onboarding";
import TutorDashboard from "./pages/tutor/Dashboard";
import AllSessions from "./pages/tutor/sessions/AllSessions";
import UpcomingSessions from "./pages/tutor/sessions/UpcomingSessions";
import PastSessions from "./pages/tutor/sessions/PastSessions";
import TutorNodeFeedback from "./pages/tutor/NodeFeedback";
import StudentOverview from "./pages/tutor/students/StudentOverview";
import Performance from "./pages/tutor/students/Performance";
import TutorStudentJourney from "./pages/tutor/students/Journey";
import TutorProfilePage from "./pages/tutor/Profile";
// Import the Schedule page directly instead of lazy loading
import TutorSchedulePage from "./pages/tutor/Schedule";
// Import profile related pages
import ResourcesPage from "./pages/tutor/profile/resources";
import TutorAccountPreferences from "./pages/tutor/AccountPreferences";
import TutorNotifications from "./pages/tutor/Notifications";
import PhotoUploadTest from "./pages/PhotoUploadTest";
import TestSVG from "./pages/TestSVG";
import StudentPageLayout from 'src/components/layouts/StudentPageLayout';
import TutorPageLayout from 'src/components/layouts/TutorPageLayout';
import AdminPageLayout from 'src/components/layouts/AdminPageLayout';
import DiagnosticSetup from "./pages/assessment/DiagnosticSetup";
import DiagnosticRunner from "./pages/assessment/DiagnosticRunner";
import DiagnosticReport from "./pages/assessment/DiagnosticReport";

// Simplified AppRoutes component using RouteConfig and Zustand
const AppRoutes = () => {
  const {
    user,
    userType,
    isOnboarded,
    isInitialized,
    isLoading,
    userStatus,
    session,
    isUserAdmin,
  } = useAuthStore();

  useEffect(() => {
    console.log("Auth state in AppRoutes:", {
      user: user ? `User ${user.id}` : "null",
      isAdmin: isUserAdmin(),
      isLoading,
      isInitialized,
      userType,
      isOnboarded,
      userStatus,
      session: session ? "Active" : "None",
    });
  }, [
    user,
    isUserAdmin,
    isLoading,
    isInitialized,
    userType,
    isOnboarded,
    userStatus,
    session,
  ]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!useAuthStore.getState().isInitialized) {
        console.warn("Loading timeout in AppRoutes - forcing initialization");
        useAuthStore.getState().setIsInitialized(true);
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Loading application...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path={ROUTES.HOME.path} element={<Index />} />
      <Route path={ROUTES.LOGIN.path} element={<Login />} />
      <Route path={ROUTES.REGISTER.path} element={<Register />} />
      <Route path={ROUTES.FORGOT_PASSWORD.path} element={<ForgotPassword />} />
      <Route path={ROUTES.FORGOT_PASSWORD_CONFIRMATION.path} element={<ForgotPasswordConfirmation />} />
      <Route path={ROUTES.RESET_PASSWORD.path} element={<ResetPassword />} />
      <Route path={ROUTES.ARTICLES.path} element={<Articles />} />
      <Route path={ROUTES.BOOK_DEMO.path} element={< BookDemo/>} />
      <Route path="/book-session/:tutorId" element={<BookDemoBySearch />} />
      <Route path={ROUTES.INQUIRY.path} element={<Inquiry />} />
      <Route path={ROUTES.FEEDBACK.path} element={<Feedback />} />
      <Route path={ROUTES.BECOME_TUTOR.path} element={<BecomeTutor />} />
      <Route path={ROUTES.TERMS.path} element={<Terms />} />
      <Route path={ROUTES.PRIVACY.path} element={<Privacy />} />
      <Route path={ROUTES.HOW_IT_WORKS.path} element={<HowItWorks />} />
      <Route path={ROUTES.RESOURCES.path} element={<Resources />} />
      <Route path={ROUTES.COURSE_MATHEMATICS.path} element={<Courses />} />
      <Route path={ROUTES.COURSE_PYTHON.path} element={<PythonCourse />} />
      <Route path={ROUTES.COURSE_JAVASCRIPT.path} element={<JavaScriptCourse />} />
      <Route path={ROUTES.ABOUT.path} element={<About />} />
      <Route path={ROUTES.CAREERS.path} element={<Careers />} />
      <Route path={ROUTES.PRICING.path} element={<Pricing />} />
      <Route path={ROUTES.TUTORS.path} element={<Tutors />} />
      <Route path={ROUTES.CONTACT.path} element={<Contact />} />
      <Route path={ROUTES.CONFIRM_EMAIL.path} element={<ConfirmEmail />} />
      <Route path={ROUTES.AUTH_CALLBACK.path} element={<ConfirmationCallback />} />
      <Route path={ROUTES.PHOTO_UPLOAD_TEST.path} element={<PhotoUploadTest />} />
      <Route path={ROUTES.TEST.path} element={<TestSVG />} />
      <Route path={ROUTES.MAINTENANCE.path} element={<UnderMaintenance estimatedTime="2 hours" message="We're upgrading our systems to serve you better. Please check back soon." />} />
      {/* Diagnostic Assessment Routes */}
      {config.features.diagnosticAssessment && (
        <>
          <Route path={ROUTES.DIAGNOSTIC_ASSESSMENT.path} element={<DiagnosticSetup />} />
          <Route path={ROUTES.DIAGNOSTIC_RUNNER.path} element={<DiagnosticRunner />} />
          <Route path={ROUTES.DIAGNOSTIC_REPORT.path} element={<DiagnosticReport />} />
        </>
      )}
      {/* Join Session Route */}
      <Route element={<ProtectedRouteWrapper routeConfig={ROUTES.JOIN_SESSION}><Outlet /></ProtectedRouteWrapper>}>
        <Route path={ROUTES.JOIN_SESSION.path} element={<JoinSession />} />
      </Route>

      {/* Onboarding */}
      <Route element={<ProtectedRouteWrapper routeConfig={ROUTES.STUDENT_ONBOARDING}><Outlet /></ProtectedRouteWrapper>}>
        <Route path={ROUTES.STUDENT_ONBOARDING.path} element={<StudentOnboarding />} />
      </Route>
      <Route element={<ProtectedRouteWrapper routeConfig={ROUTES.TUTOR_ONBOARDING}><Outlet /></ProtectedRouteWrapper>}>
        <Route path={ROUTES.TUTOR_ONBOARDING.path} element={<TutorOnboarding />} />
      </Route>

      {/* Student Layout */}
      <Route
        path={ROUTES.STUDENT_AREA.path}
        element={
          <ProtectedRouteWrapper routeConfig={ROUTES.STUDENT_AREA}>
            <StudentPageLayout />
          </ProtectedRouteWrapper>
        }
      >
        <Route index element={<StudentDashboard />} handle={{ title: 'Dashboard' }} />
        <Route path={ROUTES.STUDENT_DASHBOARD.path.replace('/student/', '')} element={<StudentDashboard />} handle={{ title: 'Dashboard' }} />
        <Route path={ROUTES.STUDENT_PROFILE.path.replace('/student/', '')} element={<StudentProfile />} handle={{ title: 'My Profile' }} />
        <Route path={ROUTES.STUDENT_SCHEDULE.path.replace('/student/', '')} element={<StudentSchedulePage />} handle={{ title: 'Schedule' }} />
        <Route path={ROUTES.STUDENT_JOURNEY.path.replace('/student/', '')} element={<StudentJourney />} handle={{ title: 'Journey' }} />
        <Route path={ROUTES.STUDENT_CURRICULUM_PROGRESS.path.replace('/student/', '')} element={<StudentCurriculumProgress />} handle={{ title: 'Curriculum progress' }} />
        <Route path={ROUTES.STUDENT_INSIGHTS.path.replace('/student/', '')} element={<StudentInsights />} handle={{ title: 'Insights' }} />
        <Route path={ROUTES.STUDENT_MATERIALS.path.replace('/student/', '')} element={<StudentMaterials />} handle={{ title: 'Materials' }} />
        <Route path={ROUTES.STUDENT_PRODUCTS.path.replace('/student/', '')} element={<Products />} handle={{ title: 'Products' }} />
        <Route path={ROUTES.STUDENT_SUBSCRIPTIONS.path.replace('/student/', '')} element={<Subscriptions />} handle={{ title: 'Subscriptions' }} />
        <Route path={ROUTES.STUDENT_ACCOUNT_PREFERENCES.path.replace('/student/', '')} element={<AccountPreferences />} handle={{ title: 'Preferences' }} />
        <Route path={ROUTES.STUDENT_NOTIFICATIONS.path.replace('/student/', '')} element={<NotificationsPage />} handle={{ title: 'Notifications' }} />
        <Route path={ROUTES.STUDENT_UPCOMING_SESSIONS.path.replace('/student/', '')} element={<StudentUpcomingSessions />} handle={{ title: 'Upcoming Sessions' }} />
        <Route path={ROUTES.STUDENT_PAST_SESSIONS.path.replace('/student/', '')} element={<StudentPastSessions />} handle={{ title: 'Past Sessions' }} />
        <Route path={ROUTES.STUDENT_REQUEST_BOOKING.path.replace('/student/', '')} element={<RequestBooking />} handle={{ title: 'Request Booking' }} />
        <Route path={ROUTES.STUDENT_NEW_SUBSCRIPTION.path.replace('/student/', '')} element={<NewSubscription />} handle={{ title: 'New Subscription' }} />
        <Route path={ROUTES.STUDENT_NEW_SUBSCRIPTION_SELECT.path.replace('/student/', '')} element={<NewSubscriptionSelect />} handle={{ title: 'Select Package' }} />
        <Route path={ROUTES.STUDENT_NEW_SUBSCRIPTION_CONFIGURE.path.replace('/student/', '')} element={<NewSubscriptionConfigure />} handle={{ title: 'Configure Subscription' }} />
        <Route path={ROUTES.STUDENT_NEW_SUBSCRIPTION_PRICING.path.replace('/student/', '')} element={<NewSubscriptionPricing />} handle={{ title: 'Subscription Pricing' }} />
        <Route path={ROUTES.STUDENT_NEW_SUBSCRIPTION_PURCHASE.path.replace('/student/', '')} element={<NewSubscriptionPurchase />} handle={{ title: 'Purchase Subscription' }} />
        <Route path={ROUTES.STUDENT_BILLING_HISTORY.path.replace('/student/', '')} element={<BillingHistory />} handle={{ title: 'Billing History' }} />
      </Route>

      {/* Tutor Layout */}
      <Route
        path={ROUTES.TUTOR_AREA.path}
        element={
          <ProtectedRouteWrapper routeConfig={ROUTES.TUTOR_AREA}>
            <TutorPageLayout />
          </ProtectedRouteWrapper>
        }
      >
        <Route index element={<TutorDashboard />} handle={{ title: 'Dashboard' }} />
        <Route path={ROUTES.TUTOR_DASHBOARD.path.replace('/tutor/', '')} element={<TutorDashboard />} handle={{ title: 'Dashboard' }} />
        <Route path={ROUTES.TUTOR_PROFILE.path.replace('/tutor/', '')} element={<TutorProfilePage />} handle={{ title: 'Tutor Profile' }} />
        <Route path={ROUTES.TUTOR_PROFILE_RESOURCES.path.replace('/tutor/', '')} element={<ResourcesPage />} handle={{ title: 'Resources' }} />
        <Route path={ROUTES.TUTOR_SCHEDULE.path.replace('/tutor/', '')} element={<TutorSchedulePage />} handle={{ title: 'Schedule' }} />
        <Route path={ROUTES.TUTOR_ACCOUNT_PREFERENCES.path.replace('/tutor/', '')} element={<TutorAccountPreferences />} handle={{ title: 'Preferences' }} />
        <Route path={ROUTES.TUTOR_NOTIFICATIONS.path.replace('/tutor/', '')} element={<TutorNotifications />} handle={{ title: 'Notifications' }} />
        <Route path={ROUTES.TUTOR_ALL_SESSIONS.path.replace('/tutor/', '')} element={<AllSessions />} handle={{ title: 'All Sessions' }} />
        <Route path={ROUTES.TUTOR_UPCOMING_SESSIONS.path.replace('/tutor/', '')} element={<UpcomingSessions />} handle={{ title: 'Upcoming Sessions' }} />
        <Route path={ROUTES.TUTOR_PAST_SESSIONS.path.replace('/tutor/', '')} element={<PastSessions />} handle={{ title: 'Past Sessions' }} />
        <Route path={ROUTES.TUTOR_STUDENT_OVERVIEW.path.replace('/tutor/', '')} element={<StudentOverview />} handle={{ title: 'Student Overview' }} />
        <Route path={ROUTES.TUTOR_STUDENT_PERFORMANCE.path.replace('/tutor/', '')} element={<Performance />} handle={{ title: 'Performance' }} />
        <Route path={ROUTES.TUTOR_STUDENT_JOURNEY.path.replace('/tutor/', '')} element={<TutorStudentJourney />} handle={{ title: 'Learning Journey' }} />
        <Route path={ROUTES.TUTOR_NODE_FEEDBACK.path.replace('/tutor/', '')} element={<TutorNodeFeedback />} handle={{ title: 'Node feedback' }} />
      </Route>

      {/* Admin Layout */}
      <Route
        path={ROUTES.ADMIN_AREA.path}
        element={
          <ProtectedRouteWrapper routeConfig={ROUTES.ADMIN_AREA}>
            <AdminPageLayout />
          </ProtectedRouteWrapper>
        }
      >
        <Route index element={<AdminDashboard />} handle={{ title: 'Dashboard' }} />
        <Route path={ROUTES.ADMIN_DASHBOARD.path.replace('/admin/', '')} element={<AdminDashboard />} handle={{ title: 'Dashboard' }} />
        <Route path={ROUTES.ADMIN_LIVE_VIEW.path.replace('/admin/', '')} element={<AdminLiveView />} handle={{ title: 'Live View' }} />
        <Route path={ROUTES.ADMIN_INQUIRIES.path.replace('/admin/', '')} element={<Inquiries />} handle={{ title: 'Inquiries' }} />
        <Route path={ROUTES.ADMIN_TUTORS.path.replace('/admin/', '')} element={<TutorManagement />} handle={{ title: 'Tutor Management' }} />
        <Route path={ROUTES.ADMIN_USERS.path.replace('/admin/', '')} element={<UserManagement />} handle={{ title: 'Users' }} />
        {/* User Management Routes */}
        <Route path={ROUTES.ADMIN_USERS_CREATE.path.replace('/admin/', '')} element={<CreateUser />} handle={{ title: 'Create User' }} />
        <Route path={ROUTES.ADMIN_USERS_LIST.path.replace('/admin/', '')} element={<ListUsers />} handle={{ title: 'List Users' }} />
        <Route path={ROUTES.ADMIN_USERS_VIEW.path.replace('/admin/', '')} element={<ViewUser />} handle={{ title: 'View User' }} />
        <Route path={ROUTES.ADMIN_USERS_EDIT.path.replace('/admin/', '')} element={<EditUser />} handle={{ title: 'Edit User' }} />
        <Route path={ROUTES.ADMIN_USERS_DELETE.path.replace('/admin/', '')} element={<DeleteUser />} handle={{ title: 'Delete User' }} />
        {/* Tutor Management Routes */}
        <Route path={ROUTES.ADMIN_TUTORS_CREATE.path.replace('/admin/', '')} element={<CreateTutor />} handle={{ title: 'Create Tutor' }} />
        <Route path={ROUTES.ADMIN_TUTORS_LIST.path.replace('/admin/', '')} element={<ListTutors />} handle={{ title: 'List Tutors' }} />
        <Route path={ROUTES.ADMIN_TUTORS_VIEW.path.replace('/admin/', '')} element={<ViewTutor />} handle={{ title: 'View Tutor' }} />
        <Route path={ROUTES.ADMIN_TUTORS_EDIT.path.replace('/admin/', '')} element={<EditTutor />} handle={{ title: 'Edit Tutor' }} />
        <Route path={ROUTES.ADMIN_TUTORS_DELETE.path.replace('/admin/', '')} element={<DeleteTutor />} handle={{ title: 'Delete Tutor' }} />
        <Route path={ROUTES.ADMIN_TUTORS_APPROVAL_QUEUE.path.replace('/admin/', '')} element={<ApprovalQueue />} handle={{ title: 'Tutor Approval Queue' }} />
        <Route path={ROUTES.ADMIN_SESSIONS.path.replace('/admin/', '')} element={<SessionManagement />} handle={{ title: 'Sessions' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_SCHEDULE.path.replace('/admin/', '')} element={<ScheduleSession />} handle={{ title: 'Schedule Session' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_EDIT.path.replace('/admin/', '')} element={<EditSession />} handle={{ title: 'Edit Session' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_ADD_USERS.path.replace('/admin/', '')} element={<AddUsersToSession />} handle={{ title: 'Add Users' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_REMOVE_USER.path.replace('/admin/', '')} element={<RemoveUserFromSession />} handle={{ title: 'Remove User' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_LIST.path.replace('/admin/', '')} element={<SessionsList />} handle={{ title: 'Session List' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_ANALYTICS.path.replace('/admin/', '')} element={<SessionAnalytics />} handle={{ title: 'Analytics' }} />
        <Route path={ROUTES.ADMIN_SESSIONS_CONFIGURATION.path.replace('/admin/', '')} element={<SessionConfiguration />} handle={{ title: 'Session Configuration' }} />
        <Route path={ROUTES.ADMIN_BATCHES.path.replace('/admin/', '')} element={<BatchManagement />} handle={{ title: 'Batches' }} />
        <Route path={ROUTES.ADMIN_ENROLLMENTS.path.replace('/admin/', '')} element={<Enrollments />} handle={{ title: 'Enrollments' }} />
        <Route path={ROUTES.ADMIN_VIEW_ENROLLMENTS.path.replace('/admin/', '')} element={<ViewEnrollments />} handle={{ title: 'View Enrollments' }} />
        <Route path={ROUTES.ADMIN_CURRICULUM_CATALOG.path.replace('/admin/', '')} element={<CurriculumCatalog />} handle={{ title: 'Curriculum Catalog' }} />
        <Route path={ROUTES.ADMIN_ENROLLMENTS_CREATE.path.replace('/admin/', '')} element={<ManualEnrollment />} handle={{ title: 'Manual Enrollment' }} />
        <Route path={`${ROUTES.ADMIN_ENROLLMENTS.path.replace('/admin/', '')}/:workflowId`} element={<EnrollmentDetail />} handle={{ title: 'Enrollment Details' }} />
        <Route path={ROUTES.ADMIN_CREATE_BATCH.path.replace('/admin/', '')} element={<CreateBatch />} handle={{ title: 'Create Batch' }} />
        <Route path={ROUTES.ADMIN_VIEW_BATCHES.path.replace('/admin/', '')} element={<ViewBatches />} handle={{ title: 'View Batches' }} />
        <Route path={ROUTES.ADMIN_EDIT_BATCH.path.replace('/admin/', '')} element={<EditBatch />} handle={{ title: 'Edit Batch' }} />
        <Route path={ROUTES.ADMIN_EXTEND_BATCH.path.replace('/admin/', '')} element={<ExtendBatchSessions />} handle={{ title: 'Add Sessions' }} />
        <Route path={ROUTES.ADMIN_DELETE_BATCH.path.replace('/admin/', '')} element={<DeleteBatch />} handle={{ title: 'Delete Batch' }} />
        <Route 
          path={ROUTES.ADMIN_DEMO_BOOKINGS.path.replace('/admin/', '')} 
          element={<DemoBookings />} 
          handle={{ title: 'Demo Bookings' }} 
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const queryClient = new QueryClient();

const App = () => {
  console.log("App component rendering");
  const isMounted = useRef(true);

  // Check for persisted navigation state - with safer cleanup
  useEffect(() => {
    // Only run if component is mounted
    if (!isMounted.current) return;

    try {
      const persistedNavigation = sessionStorage.getItem(
        "retry_login_navigation"
      );
      if (persistedNavigation) {
        console.log("Found persisted navigation state:", persistedNavigation);
      }
    } catch (error) {
      console.error("Error checking persisted navigation:", error);
    }

    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, []); // Empty dependency array for mount/unmount only

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
          <TawkToWidget />
          <Toaster />
          <Sonner />
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
